/**
 * NutritionTagFilterBody Component
 *
 * Renders nutrition tag text pills (high protein, low carbs, etc.)
 * inside the FiltersModal body for the Tags tab.
 * Each pill toggles its tag in/out of the selected set.
 *
 * Unlike diet/allergen chips these are text-only (no icons) with
 * a smaller, tighter pill style per the Figma design.
 */

// React
import { useMemo } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';
import { PILL_UNSELECTED, PILL_SELECTED } from './filterChipStyles';

/* ---- Layout constants ---- */
const WRAP_CLASSES = 'flex flex-wrap gap-4';

/* ---- Pill classes ---- */
const PILL_BASE = 'border rounded-xs px-4 py-2 cursor-pointer select-none outline-none transition-[color,background-color,border-color,transform,opacity] ease-in-out active:scale-95 focus-visible:border-brand-yellow font-inter text-sm font-normal leading-normal text-center whitespace-nowrap';

/* ---- Types ---- */
interface NutritionTagFilterBodyProps {
  selectedTags: string[];
  onToggle: (tag: string) => void;
}

interface NutritionTag {
  id: string;
  label: string;
}

/* ---- Data ---- */
// Mirrors PreDefinedFoodTags::Nutrition::ALL (app/models/concerns/pre_defined_food_tags.rb)
const NUTRITION_TAGS: NutritionTag[] = [
  { id: 'high_protein', label: 'high protein' },
  { id: 'low_carbs', label: 'low carbs' },
  { id: 'complex_carbs', label: 'complex carbs' },
  { id: 'low_saturated_fat', label: 'low saturated fat' },
  { id: 'low_cholesterol', label: 'low cholesterol' },
  { id: 'low_sodium', label: 'low sodium' },
  { id: 'high_fiber', label: 'high fibre' },
  { id: 'low_sugar', label: 'low sugar' },
  { id: 'no_sugar', label: 'no sugar' },
  { id: 'no_caffeine', label: 'no caffeine' },
];

/* ---- Component ---- */
export function NutritionTagFilterBody({ selectedTags, onToggle }: NutritionTagFilterBodyProps) {
  const selectedSet = useMemo(() => new Set(selectedTags), [selectedTags]);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-inter text-sm font-semibold leading-normal text-grey">
        Nutrition
      </span>
      <div className={WRAP_CLASSES}>
        {NUTRITION_TAGS.map(({ id, label }) => {
          const isSelected = selectedSet.has(id);
          return (
            <button
              key={id}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              className={cn(PILL_BASE, isSelected ? PILL_SELECTED : PILL_UNSELECTED)}
              onClick={() => onToggle(id)}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
